import { Commission, CommissionCreate, CommissionUpdate } from '@/models';
import { API_URL, FetchApi } from './service';

const URI = `${API_URL}/core/commissions/`;

export const CommissionServices = {
  /**
   * Récupérer toutes les commissions avec pagination et filtres
   */
  list: (params?: any) =>
    FetchApi.get(`${URI}?${new URLSearchParams(params).toString()}`),

  /**
   * Créer une nouvelle commission
   */
  create: (data: CommissionCreate) =>
    FetchApi.post(`${URI}`, data),

  /**
   * Récupérer une commission par ID
   */
  get: (id: number) =>
    FetchApi.get(`${URI}${id}/`),

  /**
   * Mettre à jour une commission
   */
  update: (id: number, data: CommissionUpdate) =>
    FetchApi.put(`${URI}${id}/`, data),

  /**
   * Supprimer une commission
   */
  delete: (id: number) =>
    FetchApi.delete(`${URI}${id}/`),

  /**
   * Ajouter des candidatures à une commission
   *
   * POST /commissions/{id}/add-applications/
   *
   * @param id - ID de la commission
   * @param applicationIds - Liste des IDs de candidatures à ajouter
   */
  addApplications: (id: number, applicationIds: number[]) =>
    FetchApi.post(`${URI}${id}/add-applications/`, { application_ids: applicationIds }),

  /**
   * Retirer des candidatures d'une commission
   *
   * POST /commissions/{id}/remove-applications/
   *
   * @param id - ID de la commission
   * @param applicationIds - Liste des IDs de candidatures à retirer
   */
  removeApplications: (id: number, applicationIds: number[]) =>
    FetchApi.post(`${URI}${id}/remove-applications/`, { application_ids: applicationIds }),

  /**
   * Démarrer une commission
   *
   * PUT /commissions/{id}/start/
   */
  start: (id: number) =>
    FetchApi.put(`${URI}${id}/start/`, {}),

  /**
   * Terminer une commission
   *
   * PUT /commissions/{id}/complete/
   *
   * @param id - ID de la commission
   * @param report - Procès-verbal de la commission (optionnel)
   * @param notes - Notes et observations (optionnel)
   */
  complete: (id: number, report?: string, notes?: string) =>
    FetchApi.put(`${URI}${id}/complete/`, { report, notes }),

  /**
   * Approuver une candidature dans la commission
   *
   * PUT /commissions/{id}/approve-application/
   *
   * @param commissionId - ID de la commission
   * @param applicationId - ID de la candidature à approuver
   */
  approveApplication: (commissionId: number, applicationId: number) =>
    FetchApi.put(`${URI}${commissionId}/approve-application/`, { application_id: applicationId }),

  /**
   * Rejeter une candidature dans la commission
   *
   * PUT /commissions/{id}/reject-application/
   *
   * @param commissionId - ID de la commission
   * @param applicationId - ID de la candidature à rejeter
   * @param reason - Raison du rejet (optionnel)
   */
  rejectApplication: (commissionId: number, applicationId: number, reason?: string) =>
    FetchApi.put(`${URI}${commissionId}/reject-application/`, {
      application_id: applicationId,
      reason
    }),

  /**
   * Récupérer les commissions programmées
   */
  scheduled: (params?: any) =>
    FetchApi.get(`${URI}?status=scheduled&${new URLSearchParams(params).toString()}`),

  /**
   * Récupérer les commissions en cours
   */
  inProgress: (params?: any) =>
    FetchApi.get(`${URI}?status=in_progress&${new URLSearchParams(params).toString()}`),

  /**
   * Récupérer les commissions terminées
   */
  completed: (params?: any) =>
    FetchApi.get(`${URI}?status=completed&${new URLSearchParams(params).toString()}`),

  /**
   * Récupérer les commissions annulées
   */
  cancelled: (params?: any) =>
    FetchApi.get(`${URI}?status=cancelled&${new URLSearchParams(params).toString()}`),

  /**
   * Enregistrer un vote pour une candidature dans la commission
   *
   * POST /commissions/{id}/vote/
   *
   * @param commissionId - ID de la commission
   * @param applicationId - ID de la candidature
   * @param justification - Justification du vote (optionnel)
   */
  vote: (commissionId: number, participantId: number, applicationIds: number[], justification?: string) =>
    FetchApi.post(`${URI}${commissionId}/vote/`, {
      commission: commissionId,
      applications: applicationIds,
      participant: participantId,
      justification: justification || ''
    }),

  /**
   * Soumettre les sélections de candidatures pour une commission
   *
   * POST /commissions/{id}/submit-selections/
   *
   * @param commissionId - ID de la commission
   * @param applicationIds - Liste des IDs de candidatures sélectionnées
   */
  submitSelections: (commissionId: number, applicationIds: number[]) =>
    FetchApi.post(`${URI}${commissionId}/submit-selections/`, {
      application_ids: applicationIds
    }),

  /**
   * Calculer les résultats de la commission (agrégation des votes)
   *
   * POST /commissions/{id}/compute-results/
   *
   * @param commissionId - ID de la commission
   */
  computeResults: (commissionId: number) =>
    FetchApi.post(`${URI}${commissionId}/compute-results/`, {}),
};